import React, { useState, useMemo, useEffect } from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption
} from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { useKeycloak } from "../config/KeycloakContext";
import { useUserContext } from "../config/UserContext";
import type { Device } from "../../../shared/types/models/device";

interface DeviceSelectorProps {
  value: Device | null;
  onChange: (value: Device | null) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
  inputTestId?: string;
  /**
   * Controls the dropdown anchor position. If unset, uses Headless UI's default ('bottom').
   * Accepts 'top', 'bottom', 'left', 'right', etc. or an anchor object (see Headless UI docs)
   */
  dropdownAnchor?: string | { to: string, gap?: number | string, offset?: number | string, padding?: number | string };
}

const DeviceSelector: React.FC<DeviceSelectorProps> = ({
  value,
  onChange,
  label = "Device",
  disabled = false,
  className = "",
  inputTestId = "device-selector",
  dropdownAnchor
}) => {
  const [query, setQuery] = useState("");
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getToken } = useKeycloak();
  const { apiBaseUrl, apiBaseUrlLoading, apiBaseUrlError } = useUserContext();
  const [showDefaultSaved, setShowDefaultSaved] = useState(false);
  const [showDropdown, setShowDropdown] = useState(true);
  const [defaultDevice, setDefaultDevice] = useState<Device | null>(null);

  useEffect(() => {
    let isMounted = true;
    if (apiBaseUrlLoading) {
      setLoading(true);
      return () => { isMounted = false; };
    }
    if (apiBaseUrlError) {
      setError(apiBaseUrlError);
      setLoading(false);
      return () => { isMounted = false; };
    }
    if (!apiBaseUrl) {
      setError("API base URL not set");
      setLoading(false);
      return () => { isMounted = false; };
    }
    setLoading(true);
    setError(null);
    getToken().then(token => {
      if (!token) {
        setError("Not authenticated");
        setLoading(false);
        return;
      }
      fetch(`${apiBaseUrl}/api/frozendeed/identifid/devices?page=0&size=100`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => {
          if (!res.ok) throw new Error("Failed to fetch devices");
          return res.json();
        })
        .then((data) => {
          if (isMounted) {
            setDevices(Array.isArray(data.content) ? data.content : []);
            setLoading(false);
          }
        })
        .catch((err) => {
          if (isMounted) {
            setError(err.message);
            setLoading(false);
          }
        });
    });
    return () => { isMounted = false; };
  }, [getToken, apiBaseUrl, apiBaseUrlLoading, apiBaseUrlError]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('deviceDefault');
      if (saved) {
        const parsed = JSON.parse(saved);
        setDefaultDevice(parsed);
        setShowDropdown(false);
        if (onChange) onChange(parsed);
      }
    } catch (e) {
      // Ignore parse errors
    }
  }, []);

  const filteredDevices = useMemo(() => {
    if (!query) return devices;
    return devices.filter(device =>
      device.serialNumber.toLowerCase().includes(query.toLowerCase())
    );
  }, [query, devices]);

  const selectedDevice = value;

  let inputPlaceholder = "Select Device";
  if (apiBaseUrlLoading || loading) inputPlaceholder = "Loading devices...";
  else if (error) inputPlaceholder = `Error: ${error}`;
  else if (apiBaseUrlError) inputPlaceholder = `Error: ${apiBaseUrlError}`;

  const isInputDisabled = disabled || loading || !!error;

  const handleSetDefault = () => {
    if (value) {
      localStorage.setItem('deviceDefault', JSON.stringify(value));
      setDefaultDevice(value);
      setShowDefaultSaved(true);
      setShowDropdown(false);
      setTimeout(() => setShowDefaultSaved(false), 2000);
    }
  };

  const handleChangeClick = () => {
    setShowDropdown(true);
  };

  return (
    <div className={clsx("w-full relative", className)}>
      {label && (
        <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta mb-1 block">
          {label}
        </label>
      )}
      {defaultDevice && !showDropdown ? (
        <div className="flex flex-col items-center w-full">
          <span className="text-lg font-plus-jakarta text-[#141435] font-semibold text-center mb-2">{defaultDevice.serialNumber}</span>
          <button
            type="button"
            className="text-xs text-[#0B4BB6] font-plus-jakarta px-3 py-1 bg-[#F4F8FF] rounded hover:bg-[#E5EDFA] focus:outline-none border border-[#D7DFEC] shadow-sm mb-2"
            style={{ minWidth: 0, fontWeight: 500, boxShadow: 'none', lineHeight: 1.1 }}
            onClick={handleChangeClick}
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <Combobox value={value} onChange={onChange} disabled={isInputDisabled} by={(a, b) => a?.uuid === b?.uuid}>
            <div className="relative">
              <ComboboxInput
                className={clsx(
                  "w-full rounded-lg border border-[#D7DFEC] bg-white py-2 pr-8 pl-3 text-sm text-[#141435] font-plus-jakarta font-semibold",
                  "focus:outline-none focus:ring-2 focus:ring-blue-200",
                  isInputDisabled && "bg-gray-100 cursor-not-allowed"
                )}
                displayValue={(device: Device | null) => (device ? device.serialNumber : "")}
                onChange={e => setQuery(e.target.value)}
                disabled={isInputDisabled}
                placeholder={inputPlaceholder}
                data-testid={inputTestId}
                data-device-id={value ? value.uuid : ''}
              />
              <ComboboxButton className="group absolute inset-y-0 right-0 flex items-center px-2.5">
                <ChevronDownIcon className="size-4 text-[#485D7D] group-data-hover:text-blue-700" />
              </ComboboxButton>
            </div>
            <ComboboxOptions
              anchor={dropdownAnchor as any}
              className={clsx(
                "w-[--input-width] rounded-xl border border-[#D7DFEC] bg-white p-1 mt-1 shadow-lg z-50",
                "empty:invisible"
              )}
            >
              {loading && (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-400">
                  Loading devices...
                </div>
              )}
              {error && !loading && (
                <div className="relative cursor-default select-none px-4 py-2 text-red-500">
                  Error: {error}
                </div>
              )}
              {!loading && !error && filteredDevices.length === 0 && (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                  No devices found
                </div>
              )}
              {filteredDevices.map(device => (
                <ComboboxOption
                  key={device.uuid}
                  value={device}
                  className={({ active, selected }) =>
                    clsx(
                      "group flex cursor-default items-center gap-2 rounded-lg px-3 py-1.5 select-none",
                      active ? "bg-blue-100 text-blue-900" : "text-[#141435]",
                      selected && "font-semibold"
                    )
                  }
                >
                  <CheckIcon className={clsx("size-4 text-blue-600", "invisible group-data-selected:visible")}/>
                  <div className="text-sm font-plus-jakarta">{device.serialNumber}</div>
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          </Combobox>
          {/* Remove Set Default button and feedback here */}
        </>
      )}
    </div>
  );
};

export default DeviceSelector; 